import { useEffect, useMemo, useState, useCallback } from "react";
import api, { extractError } from "@/lib/api";
import { SectionHeader } from "@/components/SectionHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatCompactINR, formatNumber, formatINR, initials } from "@/lib/format";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Target } from "lucide-react";

export default function TargetsPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [targets, setTargets] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ weekly_deposit: 0, monthly_deposit: 0, weekly_accounts: 0, monthly_accounts: 0 });

  const load = useCallback(async () => {
    const [u, t] = await Promise.all([api.get("/users"), api.get("/targets")]);
    setUsers(u.data);
    setTargets(t.data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const targetByUser = useMemo(() => {
    const m = {};
    for (const t of targets) m[t.user_id] = t;
    return m;
  }, [targets]);

  const assignableUsers = users.filter((u) => u.role === "sales_executive");

  const startAssign = (u) => {
    setSelected(u);
    const t = targetByUser[u.id] || {};
    setForm({
      weekly_deposit: t.weekly_deposit || 0,
      monthly_deposit: t.monthly_deposit || 0,
      weekly_accounts: t.weekly_accounts || 0,
      monthly_accounts: t.monthly_accounts || 0,
    });
    setOpen(true);
  };

  const submit = async () => {
    try {
      await api.post("/targets", { user_id: selected.id, ...form });
      toast.success(`Target updated for ${selected.name}`);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const canManage = user.role === "admin";

  if (user.role === "sales_executive") {
    const t = targetByUser[user.id] || { weekly_deposit: 0, monthly_deposit: 0, weekly_accounts: 0, monthly_accounts: 0 };
    return (
      <div data-testid="targets-page">
        <SectionHeader title="My Targets" description="Weekly & monthly deposit and account opening targets." />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <TargetCard label="Weekly Deposit" value={formatINR(t.weekly_deposit)} />
          <TargetCard label="Monthly Deposit" value={formatINR(t.monthly_deposit)} />
          <TargetCard label="Weekly Accounts" value={formatNumber(t.weekly_accounts)} />
          <TargetCard label="Monthly Accounts" value={formatNumber(t.monthly_accounts)} />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="targets-page">
      <SectionHeader title="Targets" description="Assign weekly and monthly targets to sales executives." />
      <Card className="rounded-xl border border-border bg-card p-4 shadow-sm">
        <div className="overflow-x-auto rounded-md border border-border">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40">
                <TableHead className="label-caps">Executive</TableHead>
                <TableHead className="label-caps">Weekly Deposit</TableHead>
                <TableHead className="label-caps">Monthly Deposit</TableHead>
                <TableHead className="label-caps">Weekly Accounts</TableHead>
                <TableHead className="label-caps">Monthly Accounts</TableHead>
                {canManage && <TableHead className="label-caps text-right">Action</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {assignableUsers.map((u) => {
                const t = targetByUser[u.id];
                return (
                  <TableRow key={u.id} data-testid={`target-row-${u.employee_id}`} className="hover:bg-muted/40">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8"><AvatarFallback className="bg-primary/10 text-xs font-semibold text-primary">{initials(u.name)}</AvatarFallback></Avatar>
                        <div>
                          <div className="font-medium">{u.name}</div>
                          <div className="text-xs text-muted-foreground tabular-nums">{u.employee_id}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="tabular-nums">{t ? formatCompactINR(t.weekly_deposit) : "—"}</TableCell>
                    <TableCell className="tabular-nums">{t ? formatCompactINR(t.monthly_deposit) : "—"}</TableCell>
                    <TableCell className="tabular-nums">{t ? formatNumber(t.weekly_accounts) : "—"}</TableCell>
                    <TableCell className="tabular-nums">{t ? formatNumber(t.monthly_accounts) : "—"}</TableCell>
                    {canManage && (
                      <TableCell className="text-right">
                        <Button size="sm" variant="secondary" onClick={() => startAssign(u)} data-testid={`assign-target-${u.employee_id}`}>
                          <Target className="mr-2 h-4 w-4" /> Assign
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
              {assignableUsers.length === 0 && (
                <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">No sales executives found.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="target-dialog">
          <DialogHeader><DialogTitle>Assign target — {selected?.name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <NumField label="Weekly Deposit (₹)" value={form.weekly_deposit} onChange={(v) => setForm({ ...form, weekly_deposit: v })} testid="target-weekly-deposit" />
            <NumField label="Monthly Deposit (₹)" value={form.monthly_deposit} onChange={(v) => setForm({ ...form, monthly_deposit: v })} testid="target-monthly-deposit" />
            <NumField label="Weekly Accounts" value={form.weekly_accounts} onChange={(v) => setForm({ ...form, weekly_accounts: v })} testid="target-weekly-accounts" />
            <NumField label="Monthly Accounts" value={form.monthly_accounts} onChange={(v) => setForm({ ...form, monthly_accounts: v })} testid="target-monthly-accounts" />
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} data-testid="target-submit">Save target</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NumField({ label, value, onChange, testid }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input
        type="number"
        min={0}
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(Number(e.target.value || 0))}
        className="tabular-nums"
      />
    </div>
  );
}

function TargetCard({ label, value }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
      <div className="label-caps">{label}</div>
      <div className="metric-value mt-4 text-3xl">{value}</div>
    </div>
  );
}
